import React, { useRef } from 'react';

import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import { PrivilegeTypes, GroupMode, ResponseItem } from '@bringg/types';
import { Checkbox, Spinner } from '@bringg/react-components';
import { DetailedCellProps, useRowHover, ViewItemButton } from '@bringg-frontend/monitoring-shared';

import { Link } from 'bringg-web/components';
import useOrderExplorerStore from '../stores/use-order-explorer-store';
import { isOneLevelGroup, isTwoLevelsGroup } from '../../shared/utils/grouping';
import { useHasAccess } from 'bringg-web/utils/privileges';
import ColorIndicator from '../../shared/components/color-indicator/color-indicator';

import styles from './order-row.module.scss';

const OrderRow = ({ row }: DetailedCellProps<ResponseItem>) => {
	const { id } = row;
	const ref = useRef<HTMLDivElement>(null);
	useRowHover(ref, id);
	const {
		ordersMapStore: { setMarkersToViewOnMap, markersToViewOnMap },
		selectedTasksStore: { setSelectedTasks, isTaskSelected }
	} = useOrderExplorerStore();
	const privilegeToViewTask = useHasAccess(PrivilegeTypes.VIEW_TASK);

	const task = getRootEnv().dashboardSdk.sdk.v2.tasks.get(id);

	const { orderExplorerStore } = useOrderExplorerStore();

	if (!task) {
		return <Spinner />;
	}

	const isSelected = isTaskSelected(task.id);
	const isItemViewable =
		orderExplorerStore.currentTableDefinition.groupMode === GroupMode.Team ||
		orderExplorerStore.currentTableDefinition.groupMode === GroupMode.None;

	const isInView = markersToViewOnMap.has(task.id);
	const shouldShowGroupColor = ![GroupMode.Team, GroupMode.None].includes(
		orderExplorerStore.currentTableDefinition.groupMode
	);

	const taskTitle = task.external_id || task.title || task.id;

	return (
		<div
			className={classnames(styles.row, styles.node, `row-${task.id}`, {
				[styles.firstLevelNode]: isOneLevelGroup(orderExplorerStore.currentTableDefinition.groupMode),
				[styles.secondLevelNode]: isTwoLevelsGroup(orderExplorerStore.currentTableDefinition.groupMode)
			})}
			ref={ref}
			data-test-id={`row-${id}`}
		>
			{shouldShowGroupColor && (
				<ColorIndicator color={orderExplorerStore.getItemColor(task.id)} dataTestId={`color-row-${task.id}`} />
			)}
			<Checkbox
				className={styles.checkbox}
				checked={isSelected}
				onChange={() => {
					setSelectedTasks(task, true);
				}}
				data-test-id={`select-${id}`}
			/>
			{isItemViewable && (
				<ViewItemButton
					onClick={(rowId, isChecked) => {
						setMarkersToViewOnMap([rowId], isChecked);
					}}
					isInView={isInView}
					item={row}
				/>
			)}
			{privilegeToViewTask ? (
				<Link to={`history/${task.id}`} openInNewTab className={styles.title}>
					{taskTitle}
				</Link>
			) : (
				<div className={styles.title}>{taskTitle}</div>
			)}
		</div>
	);
};

export default observer(OrderRow);
